<script lang="ts">
  import { keyboard } from '../keys/keyboard.svelte';
  import type { Block } from '../engine/types';
  import type { FileDiff } from '../state/diffs.svelte';
  import { changedLines, imageMime, type PreviewKind } from '../logic/preview';
  import { rawBytes, type RawSource } from '../logic/rawbytes';
  import ImagePreview from './ImagePreview.svelte';
  import MarkdownPreview from './MarkdownPreview.svelte';

  interface Props {
    kind: PreviewKind;
    path: string;
    mode: 'split' | 'unified';
    /** The file's change in this comparison; null for a file it does not change. */
    status: 'added' | 'removed' | 'modified' | 'renamed' | 'copied' | 'changed' | 'unchanged' | null;
    /** Text and blocks (Markdown). */
    diff: FileDiff | undefined;
    blocks: Block[];
    /** Where each side's bytes are (images); null when the side has no file. */
    oldSource: (() => Promise<RawSource | null>) | null;
    newSource: (() => Promise<RawSource | null>) | null;
    /** Commits each side shows (Markdown links and images). */
    oldRef: string | null;
    newRef: string | null;
    oldLabel: string;
    newLabel: string;
    /** The old side's path (renames). */
    previous?: string | null;
    owner: string;
    repo: string;
    webUrl: string;
  }
  let {
    kind,
    path,
    mode,
    status,
    diff,
    blocks,
    oldSource,
    newSource,
    oldRef,
    newRef,
    oldLabel,
    newLabel,
    previous = null,
    owner,
    repo,
    webUrl,
  }: Props = $props();
  const sourceKey = $derived(keyboard.key({ kind: 'present', what: 'preview' }));

  const unchanged = $derived(status === null);
  const hasOld = $derived(!unchanged && status !== 'added');
  const hasNew = $derived(status !== 'removed');
  /** One column: unified mode, a file this comparison does not change, or only one side exists. */
  const single = $derived(mode === 'unified' || unchanged);

  const bytesOf = (src: (() => Promise<RawSource | null>) | null) =>
    src
      ? async () => {
          const s = await src();
          return s ? rawBytes(owner, repo, s) : null;
        }
      : null;
  const oldLoad = $derived(hasOld ? bytesOf(oldSource) : null);
  const newLoad = $derived(hasNew ? bytesOf(newSource) : null);
  const mime = $derived(imageMime(path) ?? 'application/octet-stream');

  const join = (lines: string[]) => (lines.length ? lines.join('\n') + '\n' : '');
  const oldText = $derived(hasOld && diff ? join(diff.oldLines) : null);
  const newText = $derived(hasNew && diff ? join(diff.newLines) : null);
  const oldChanged = $derived(unchanged ? null : changedLines(blocks, 'old'));
  const newChanged = $derived(unchanged ? null : changedLines(blocks, 'new'));
  const changeCount = $derived(blocks.filter((b) => b.kind === 'change').length);
</script>

<div class="file-preview" class:split={!single}>
  <p class="preview-note" role="note">
    <span class="preview-tag">Preview</span>
    <span>Read-only. Switch to Source{#if sourceKey} (<kbd>{sourceKey}</kbd>){/if} to comment on lines.</span>
    {#if kind === 'markdown' && !unchanged && changeCount}
      <span class="preview-legend"><i aria-hidden="true"></i>Changed sections</span>
    {/if}
  </p>
  {#if kind === 'image'}
    <div class="img-compare">
      {#if single}
        {#if hasNew}
          <ImagePreview label={newLabel} {mime} load={newLoad} />
        {:else}
          <ImagePreview label="{oldLabel} · deleted" {mime} load={oldLoad} />
        {/if}
      {:else}
        <ImagePreview label={oldLabel} {mime} load={oldLoad} empty="No base version: a new file" />
        <ImagePreview label={newLabel} {mime} load={newLoad} empty="Deleted in this version" />
      {/if}
    </div>
  {:else if single}
    {#if hasNew}
      <MarkdownPreview text={newText} {path} commit={newRef} {owner} {repo} {webUrl} changed={newChanged} />
    {:else}
      <MarkdownPreview text={oldText} path={previous ?? path} commit={oldRef} {owner} {repo} {webUrl} label="{oldLabel} · deleted" />
    {/if}
  {:else}
    <div class="md-compare">
      <MarkdownPreview
        text={oldText}
        path={previous ?? path}
        commit={oldRef}
        {owner}
        {repo}
        {webUrl}
        changed={oldChanged}
        empty="No base version: a new file"
      />
      <MarkdownPreview text={newText} {path} commit={newRef} {owner} {repo} {webUrl} changed={newChanged} empty="Deleted in this version" />
    </div>
  {/if}
</div>
